import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { CheckCircle2, Clock, Download } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import {
  downloadWaiverCopy,
  getWaiver,
  getWaiverPdf,
  signatureKeys,
  submitWaiver,
} from './signaturesApi';
import PublicShell from './components/PublicShell';
import SigningFlow from './components/SigningFlow';
import { loadPdf } from './lib/pdf';

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/**
 * The public waiver flow at /w/:slug — anyone with the link identifies
 * themselves, signs, and receives their OWN sealed copy (OtterWaiver's bulk
 * PDF waiver signing).
 */
export default function WaiverSignPage() {
  const { slug } = useParams();
  const [identity, setIdentity] = useState(null);
  const [consent, setConsent] = useState(false);

  // Audit moments for the Certificate of Completion. `viewedAt` is the first
  // render of this page; `consentedAt` the first tick of the consent box. Refs,
  // not state: they must never re-render, and must never move once recorded.
  const viewedAt = useRef(new Date().toISOString());
  const consentedAt = useRef(null);
  const onConsentChange = (checked) => {
    if (checked && !consentedAt.current) consentedAt.current = new Date().toISOString();
    setConsent(checked);
  };
  const [pdf, setPdf] = useState(null);
  const [downloadToken, setDownloadToken] = useState(null);
  const [requestKey] = useState(() => crypto.randomUUID());

  const waiver = useQuery({
    queryKey: signatureKeys.waiver(slug),
    queryFn: () => getWaiver(slug),
    retry: false,
  });

  const pdfBytes = useQuery({
    queryKey: signatureKeys.waiverPdf(slug),
    queryFn: () => getWaiverPdf(slug),
    enabled: Boolean(identity),
    staleTime: Infinity,
  });

  useEffect(() => {
    let cancelled = false;
    if (pdfBytes.data) {
      loadPdf(pdfBytes.data).then((loaded) => {
        if (!cancelled) setPdf(loaded);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [pdfBytes.data]);

  const form = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', phone: '' },
  });

  const submit = useMutation({
    mutationFn: ({ values, audit }) =>
      submitWaiver({
        slug,
        ...identity,
        requestKey,
        consent: true,
        values,
        audit: {
          ...audit,
          viewedAt: viewedAt.current,
          consentedAt: consentedAt.current ?? undefined,
        },
      }),
    onSuccess: (data) => {
      setDownloadToken(data.downloadToken);
      window.scrollTo({ top: 0 });
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  if (waiver.isLoading) {
    return (
      <PublicShell>
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      </PublicShell>
    );
  }

  if (waiver.error) {
    return (
      <PublicShell title="Document unavailable">
        <div className="mx-auto max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <Clock className="mx-auto h-12 w-12 text-brand-600" />
          <h2 className="text-lg font-semibold text-slate-900">This document isn&apos;t available</h2>
          <p className="text-sm text-slate-500">
            {getErrorMessage(waiver.error, 'The link may be invalid, expired, or withdrawn.')}
          </p>
        </div>
      </PublicShell>
    );
  }

  const { document: doc, fields } = waiver.data;
  const needsPhone = fields.some((f) => f.kind === 'phone');

  if (downloadToken) {
    return (
      <PublicShell title={doc.title}>
        <div className="mx-auto max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
          <h2 className="text-lg font-semibold text-slate-900">Thank you for signing!</h2>
          <p className="text-sm leading-relaxed text-slate-500">
            Your signed copy has been emailed to {identity?.email}. You can also download it now.
          </p>
          <Button
            onClick={() =>
              downloadWaiverCopy(downloadToken, doc.title).catch((err) =>
                toast.error(getErrorMessage(err)),
              )
            }
          >
            <Download className="h-4 w-4" /> Download my signed copy
          </Button>
        </div>
      </PublicShell>
    );
  }

  if (!identity) {
    return (
      <PublicShell title={doc.title} subtitle="Please identify yourself to sign">
        <form
          onSubmit={form.handleSubmit((values) =>
            setIdentity({
              firstName: values.firstName.trim(),
              lastName: values.lastName.trim() || undefined,
              email: values.email.trim(),
              phone: values.phone.trim() || undefined,
            }),
          )}
          className="mx-auto max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8"
        >
          <h2 className="text-lg font-semibold text-slate-900">
            Sign &ldquo;{doc.title}&rdquo;
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="First name"
              error={form.formState.errors.firstName?.message}
              {...form.register('firstName', { required: 'Required' })}
            />
            <Input label="Last name" {...form.register('lastName')} />
          </div>
          <Input
            label="Email"
            type="email"
            hint="Your signed copy is sent here"
            error={form.formState.errors.email?.message}
            {...form.register('email', {
              required: 'Required',
              pattern: { value: EMAIL_RE, message: 'Invalid email' },
            })}
          />
          {needsPhone && <Input label="Phone" {...form.register('phone')} />}
          <label className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => onConsentChange(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-brand-600"
            />
            I agree to use electronic records and signatures.
          </label>
          <Button type="submit" className="w-full" disabled={!consent}>
            Review & sign
          </Button>
        </form>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      title={doc.title}
      subtitle={`Signing as ${identity.firstName} ${identity.lastName ?? ''} · ${identity.email}`}
    >
      {!pdf ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <SigningFlow
          pdf={pdf}
          fields={fields}
          signer={identity}
          submitting={submit.isPending}
          onSubmit={(values, audit) => submit.mutate({ values, audit })}
        />
      )}
    </PublicShell>
  );
}
